import axios from 'axios';
import { auth } from '../firebaseConfig';
import { waitForMonitoringCorrectionJob } from './monitoringCorrectionJob';

// --- Environment Configuration ---
// The application now runs exclusively with a local backend (Node.js/Express).
// There is no dependency on Google Cloud Functions.

// In production (VPS + Nginx), we use a relative path '/api' which is proxied by Nginx to the backend.
// In development (npm start), we connect directly to the local server port (default 3001).
// Note: REACT_APP_API_TARGET is no longer used.
const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction ? '/api' : (process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:3001/api');

// --- API Client ---
// A single Axios instance configured for the correct environment.
const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Add a request interceptor to include the Firebase Auth token
apiClient.interceptors.request.use(
    async (config) => {
        const user = auth.currentUser;
        if (user) {
            try {
                const token = await user.getIdToken();
                config.headers.Authorization = `Bearer ${token}`;
            } catch (error) {
                console.error('Error getting auth token:', error);
            }
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Retry once on transient gateway errors (502/503/504), common right after a backend
// restart/deploy, before surfacing the failure to the caller.
const RETRYABLE_STATUS_CODES = [502, 503, 504];
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { config, response } = error;
        if (config && response && RETRYABLE_STATUS_CODES.includes(response.status) && !config.__retried && !config.skipGatewayRetry) {
            config.__retried = true;
            await new Promise((resolve) => setTimeout(resolve, 1500));
            return apiClient(config);
        }
        return Promise.reject(error);
    }
);

// --- Generic Function Caller ---
// This new implementation removes the dependency on Firebase Functions for API calls
// and uses a single, environment-aware Axios client.
export const callApi = async (functionName, payload, options = {}) => {
    try {
        console.log(`Calling endpoint: ${apiClient.defaults.baseURL}/${functionName}`);
        // The functionName itself (e.g., "logActivity") is passed as the path.
        // Axios will correctly join it with the baseURL.
        const result = await apiClient.post(functionName, payload, options);
        return result.data;
    } catch (error) {
        console.error(`Error calling function ${functionName}:`, error);
        // Re-throw the error with a more descriptive message, but preserve the response
        const newError = new Error(`Failed to execute ${functionName}: ${error.message}`);
        if (error.code === 'ERR_CANCELED') newError.name = 'AbortError';
        newError.response = error.response;
        throw newError;
    }
};

// --- Exported Service Functions ---

export const getMonitoringBoxes = payload => callApi('monitoring/boxes', payload, { timeout: 20000 });
export const getMonitoringMapClients = signal => callApi('monitoring/map-clients', {}, { timeout: 180000, signal, skipGatewayRetry: true });
export const getMonitoringMapCtos = signal => callApi('monitoring/map-ctos', {}, { timeout: 180000, signal, skipGatewayRetry: true });
export const getMonitoringReferences = () => callApi('monitoring/references', {});
export const prepareMonitoringServiceOrder = clientId => callApi('monitoring/service-orders/prepare', { clientId });
export const prepareMonitoringBoxServiceOrder = boxId => callApi('monitoring/service-orders/prepare', { boxId });
export const createMonitoringServiceOrder = payload => callApi('monitoring/service-orders/create', payload, { skipGatewayRetry: true });
export const getMonitoringOlts = () => callApi('monitoring/olts', {}, { timeout: 20000 });
export const updateMonitoringBox = payload => callApi('monitoring/box', payload);
const runMonitoringCorrectionJob = async (route, payload, options = {}) => {
    const initial = await callApi(route, payload, { skipGatewayRetry: true, signal: options.signal });
    return waitForMonitoringCorrectionJob(initial, { ...options, getStatus: (jobId, signal) => callApi('monitoring/box-cities/status', { jobId }, { signal }) });
};
export const previewMonitoringBoxCities = options => runMonitoringCorrectionJob('monitoring/box-cities/preview', {}, options);
export const correctMonitoringBoxCities = (payload, options) => runMonitoringCorrectionJob('monitoring/box-cities/apply', payload, options);
export const previewMonitoringPorts = options => runMonitoringCorrectionJob('monitoring/ports/preview', {}, options);
export const correctMonitoringPorts = (payload, options) => runMonitoringCorrectionJob('monitoring/ports/apply', payload, options);
// A gateway failure can originate after IXC has already processed the edit.
// Require a fresh user action instead of automatically repeating this write.
export const updateMonitoringClient = payload => callApi('monitoring/client', payload, { skipGatewayRetry: true });

export const setUserRole = async (userId, newRole) => {
    return await callApi('setUserRole', { userId, newRole });
};

export const reverseGeocode = async (lat, lng) => {
    return await callApi('reverseGeocode', { lat, lng });
};

export const checkCoverage = async (lat, lng, addressText = '', locationType = 'street', locationId = '') => {
    return await callApi('checkCoverage', { lat, lng, addressText, locationType, locationId });
};

export const scrapeCpflUrl = async (url) => {
    return await callApi('scrapeCpflUrl', { url });
};

export const triggerKpiCalculation = async (threshold) => {
    return await callApi('triggerKpiCalculation', { threshold });
};

export const updateNetworkOutage = (eventId, action) => callApi('network-outages/action', { eventId, action });
export const refreshNetworkOutages = () => callApi('network-outages/refresh', {});

export const getDeepseekData = async (text) => {
    const result = await callApi('getDeepseekData', { text });
    // The callApi function returns the whole data object { success, data }
    // The original service was returning result.data, so we replicate that here.
    if (result.success) {
        return result.data;
    }
    // If not successful, callApi throws an error, which is the desired behavior.
    return null;
};

export const startPoleLocationUpdate = async () => {
    return await callApi('update-pole-locations');
};

export const getTaskStatus = async (taskId) => {
    try {
        const result = await apiClient.get(`task-status/${taskId}`);
        return result.data;
    } catch (error) {
        console.error(`Error getting status for task ${taskId}:`, error);
        throw new Error(`Failed to get task status: ${error.message}`);
    }
};

export const cancelTask = async (taskId) => {
    return await callApi('cancel-task', { taskId });
};

export const refreshDashboardStats = async () => {
    return await callApi('refresh-dashboard-stats');
};

export const getEmailSettings = async () => {
    try {
        const result = await apiClient.get('admin/email-settings');
        return result.data;
    } catch (error) {
        console.error("Error getting email settings:", error);
        throw error;
    }
};

export const forceEmailSync = async () => {
    try {
        const result = await apiClient.post('force-email-sync');
        return result.data;
    } catch (error) {
        console.error("Error forcing email sync:", error);
        throw error;
    }
};

export const triggerBiCalculation = async () => {
    return await callApi('triggerBiCalculation');
};

export const createLocationSession = async () => {
    return await callApi('create-location-session', {});
};

export const updateLocationSession = async (sessionId, lat, lng) => {
    // This might be called without auth (client side), callApi handles it if user is null
    return await callApi('update-location-session', { sessionId, lat, lng });
};

export const getLocationSession = async (sessionId) => {
    try {
        // Use apiClient directly for GET to avoid 'post' default in callApi
        // But we need to handle the baseURL correctly. apiClient has it configured.
        const response = await apiClient.get(`location-session/${sessionId}`);
        return response.data;
    } catch (error) {
        console.error("Error getting location session:", error);
        throw error;
    }
};

export const geocodeAddress = async (address) => {
    return await callApi('geocode', { address });
};

export const getSystemStatus = async () => {
    try {
        const result = await apiClient.get('system-status');
        return result.data;
    } catch (error) {
        console.error("Error getting system status:", error);
        throw error;
    }
};

export const searchBuildings = async (query) => {
    return await callApi('searchBuildings', { query });
};

export const saveEmailSettings = async (settings) => {
    try {
        const result = await apiClient.post('admin/email-settings', settings);
        return result.data;
    } catch (error) {
        console.error("Error saving email settings:", error);
        throw error;
    }
};

export const getAiSettings = async () => {
    const result = await apiClient.get('admin/ai-settings');
    return result.data;
};

export const saveAiSettings = async (mode) => {
    const result = await apiClient.post('admin/ai-settings', { mode });
    return result.data;
};

export const analyzeChurnAI = async (force = false) => {
    return await callApi('analyze-churn-ai', { force });
};

export const getOpaAttendantsRanking = async (month, year, setor, force = false) => {
    try {
        const params = { month, year, setor };
        if (force) params.force = 'true';
        const result = await apiClient.get('/opa/ranking', { params });
        return result.data;
    } catch (error) {
        console.error("Error fetching OPA ranking:", error);
        throw error;
    }
};

export const getOpaAttendantPhoto = async (url, signal) => {
    const response = await apiClient.post('/opa/photo', { url }, { responseType: 'blob', signal });
    return response.data;
};


export const getOpaEvolution = async (attendantId, range) => {
    try {
        const result = await apiClient.get(`/opa/evolution/${attendantId}`, { params: { range } });
        return result.data;
    } catch (error) {
        console.error("Error fetching OPA evolution:", error);
        throw error;
    }
};

export const getOpaAttendantAttendances = async (attendantId, month, year) => {
    try {
        const result = await apiClient.get(`/opa/attendant-attendances/${attendantId}`, { params: { month, year } });
        return result.data;
    } catch (error) {
        console.error("Error fetching OPA attendant attendances:", error);
        throw error;
    }
};

export const getAttendantAIAnalysis = async (attendantId) => {
    try {
        const result = await apiClient.get(`/opa/analyze-attendant/${attendantId}`);
        return result.data;
    } catch (error) {
        console.error("Error fetching attendant AI analysis:", error);
        throw error;
    }
};

export const triggerAttendantAIAnalysis = async (attendantId, finalScore = null) => {
    try {
        const result = await apiClient.post(`/opa/analyze-attendant/${attendantId}`, { finalScore });
        return result.data;
    } catch (error) {
        console.error("Error triggering attendant AI analysis:", error);
        throw error;
    }
};

